import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tag, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLURAL = { contact: 'contacts', company: 'companies' };
const LIFECYCLE = ['subscriber', 'lead', 'mql', 'sql', 'opportunity', 'customer'];

const TagsLifecycle = ({ objectType, recordId, tags, lifecycle, onSaved }) => {
  const [localTags, setLocalTags] = useState(tags || []);
  const [stage, setStage] = useState(lifecycle || 'none');
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocalTags(tags || []); setStage(lifecycle || 'none'); }, [tags, lifecycle, recordId]);

  const addTag = () => {
    const t = input.trim().toLowerCase();
    if (t && !localTags.includes(t)) setLocalTags([...localTags, t]);
    setInput('');
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/${PLURAL[objectType]}/${recordId}`, {
        tags: localTags,
        lifecycle_stage: stage === 'none' ? null : stage,
      });
      toast.success('Saved');
      if (onSaved) onSaved();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="border-t border-slate-800 pt-4 space-y-3" data-testid="tags-lifecycle">
      <div>
        <Label className="text-slate-300 mb-1.5 block flex items-center gap-2"><Tag className="w-4 h-4" /> Tags</Label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {localTags.map(t => (
            <Badge key={t} variant="outline" className="text-[11px] border-violet-700/50 text-violet-300 capitalize flex items-center gap-1" data-testid={`tag-${t}`}>
              {t}
              <button onClick={() => setLocalTags(localTags.filter(x => x !== t))} className="hover:text-rose-400"><X className="w-2.5 h-2.5" /></button>
            </Badge>
          ))}
          {localTags.length === 0 && <span className="text-xs text-slate-600 italic">No tags</span>}
        </div>
        <div className="flex gap-2">
          <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} placeholder="Add a tag…" className="bg-slate-900 border-slate-800 h-9" data-testid="tag-input" />
          <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700 h-9" onClick={addTag} data-testid="add-tag-btn">Add</Button>
        </div>
      </div>
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <Label className="text-slate-300 mb-1.5 block">Lifecycle stage</Label>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger className="bg-slate-900 border-slate-800 h-9" data-testid="lifecycle-select"><SelectValue placeholder="-" /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              <SelectItem value="none">- none -</SelectItem>
              {LIFECYCLE.map(s => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-500 h-9" data-testid="save-tags-lifecycle">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default TagsLifecycle;
